import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, CircularProgress, Alert, Snackbar } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store';
import { selectSelectedStudent, selectIsMobileClassroom } from '../store/selectors/studentSelectors';
import { selectCurrentSession, fetchSessionData, updateSessionAnswer } from '../store/slices/sessionSlice';
import Header from '../components/Header';
import TestSidebar from '../components/TestSidebar';
import QuestionCard from '../components/QuestionCard';
import {
  fetchPackageQuestions,
  trackQuestionLoad,
  submitAnswer,
  markQuestionForReview,
  endTestSession
} from '../services/testSessionService';
import { audioPreloader } from '../services/audioPreloader';

// Utility function to lighten/darken colors
const lightenColor = (color: string, amount: number) => {
  const usePound = color[0] === '#';
  const col = usePound ? color.slice(1) : color;
  const num = parseInt(col, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;
  r = r > 255 ? 255 : r < 0 ? 0 : r;
  g = g > 255 ? 255 : g < 0 ? 0 : g;
  b = b > 255 ? 255 : b < 0 ? 0 : b;
  return (usePound ? '#' : '') + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
};

// Our internal Question type used in state
interface Question {
  id: string;
  text: string;
  options: string[];
  correctAnswerIndex?: number;
  explanation?: string;
  points: number;
  isVisited: boolean;
  isMarkedForReview: boolean;
  selectedOption?: number;
  mediaUrl?: string; // Add mediaUrl support
}

const TestTakingPage: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const isMobileClassroom = useSelector(selectIsMobileClassroom);
  // Get session data from Redux store instead of making API call
  const currentSession = useSelector(selectCurrentSession);
  const selectedTest = useSelector((state: RootState) => state.tests.selectedTest);
  
  // Get sessionId from the selected test in Redux store
  const sessionId = selectedTest?.sessionId;
  
  // Add ref to prevent duplicate API calls
  const isFetchingQuestionsRef = React.useRef(false);
  // Always holds the latest countdown value so handleSubmitTest can read it
  // without taking timeRemaining as a dependency (which ticks every second).
  const timeRemainingRef = React.useRef(30 * 60);
  
  // Add state for package ID which will be fetched from session details
  // Remove this line:
  // const [packageId, setPackageId] = useState<string>('');
  
  const navigate = useNavigate();
  const selectedStudent = useSelector(selectSelectedStudent);
  // Remove this duplicate line:
  // const selectedTest = useSelector((state: RootState) => state.tests.selectedTest);
  
  // State management
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [previousQuestionId, setPreviousQuestionId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(30 * 60); // Default to 30 minutes for NextZenOlympiad tests
  const [questions, setQuestions] = useState<Question[]>([]);
  // Removed visitedQuestions state as it's not used
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Get test title and color from store
  const testTitle = selectedTest?.title || 'Test';
  const testColor = selectedTest?.color || '#1976d2'; // Get color from selected test

  // Debug information
  useEffect(() => {
  }, [sessionId, selectedTest, selectedStudent]);  // Changed from testId

  // Effect to load session details, questions, and previous activities when resuming
  useEffect(() => {
    
    if (!sessionId) {
      setError('No test session found. Please start a test first.');
      setTimeout(() => {
        navigate('/start-test');
      }, 2000);
      setIsLoading(false);
      return;
    }
    
    const loadSessionAndQuestions = async () => {
      // Prevent duplicate calls
      if (isFetchingQuestionsRef.current) {
        return;
      }
      
      setIsLoading(true);
      setError('');
      
      try {
        isFetchingQuestionsRef.current = true;
        
        // If no session in store, try to fetch it
        let session = currentSession;
        if (!session && sessionId) {
          try {
            const fetchResult = await dispatch(fetchSessionData(sessionId) as any);
            session = fetchResult.payload;
          } catch (error) {
            setError('Failed to load session data. Please try starting the test again.');
            setTimeout(() => {
              navigate('/start-test');
            }, 2000);
            setIsLoading(false);
            return;
          }
        }
        
        if (!session) {
          setError('Session not found or could not be loaded');
          setTimeout(() => {
            navigate('/start-test');
          }, 2000);
          setIsLoading(false);
          return;
        }

        // Check for packageId in session
        if (!session.packageId) {
          setError('Invalid session data: missing package ID');
          setIsLoading(false);
          return;
        }
        
        
        // Fetch questions from the package FIRST before processing activities
        let fetchedQuestions = [];
        try {
          fetchedQuestions = await fetchPackageQuestions(session.packageId) || [];
        } catch (questionsError) {
          setError('Failed to load test questions');
          setIsLoading(false);
          return;
        }
        
        if (!fetchedQuestions || fetchedQuestions.length === 0) {
          setError('No questions found for this test');
          setIsLoading(false);
          return;
        }
        
        // AFTER getting questions, process activities and answers for state restoration
        
        // Defensively get activities from session (either directly included or generated from answers)
        const activities = (session.activities || []);

        // Get current question index from session if available, default to 0
        const currentQuestionIndexFromSession = session.currentQuestionIndex || 0;

        // Create data structures for state restoration
        const answerMap = new Map<string, number>();
        const markedQuestions = new Set<string>();
        const visitedIndices = new Set<number>([0]); // Always mark first question as visited by default
        
        // Don't restore visited questions - users start fresh with only first question visited
        
        // Trust the session's currentQuestionIndex as the authoritative position
        let lastViewedQuestionIndex = currentQuestionIndexFromSession;
        
        try {
          // First, look at direct answers data if available (backward compatibility)
          if (session.answers && session.answers.length > 0) {
            session.answers.forEach(answer => {
              // Only process valid answers (non-null)
              if (answer && answer.questionId && answer.answer !== null) {
                answerMap.set(answer.questionId, answer.answer);
              }
            });
          }

          // Then process activities for complete state restoration (including marked for review)
          if (activities && activities.length > 0) {
            activities.forEach((activity: any, index: number) => {
              try {
                if (!activity || !activity.questionId) return;
                
                const questionId = activity.questionId;
                
                // If answer is provided in activity, store it (overrides direct answers)
                if (activity.answerIndex !== undefined) {
                  answerMap.set(questionId, activity.answerIndex);
                }
                
                // If question is marked for review, track it
                if (activity.isMarkedForReview) {
                  markedQuestions.add(questionId);
                }
                
                // Note: We trust session.currentQuestionIndex as the authoritative position
                // Activity-based position tracking has been removed to prevent navigation bugs
              } catch (activityError) {
                // Don't let one bad activity break the entire restoration process
              }
            });
          }
        } catch (stateRestorationError) {
          // Continue with default state in case of error
          // We don't want to block test taking due to state restoration issues
        }
        
        
        // Define questionCount to use outside of try block
        let questionCount = 0;
        
        try {
          // Map API questions to our internal Question format
          const processedQuestions = fetchedQuestions.map((q: any, index: number) => {
            const questionId = q.id || `question-${index}`;
            const selectedOption = answerMap.get(questionId);
            const isMarkedForReview = markedQuestions.has(questionId);
            const isVisited = visitedIndices.has(index);
            
            return {
              id: questionId,
              text: q.question || q.text || '', // Map API 'question' field to 'text' for UI
              options: q.options || [],
              correctAnswerIndex: q.correctAnswer !== null ? q.correctAnswer : undefined,
              explanation: q.explanation || '',
              points: q.score || 0,
              selectedOption: selectedOption,  // Using selectedOption instead of userAnswer
              isMarkedForReview: isMarkedForReview,
              isVisited: isVisited,
              mediaUrl: q.mediaUrl // Include mediaUrl from API response
            } as Question;
          });
          
          // Set test duration and time left with good defaults. totalDuration,
          // timeRemaining and timeLeft are all in SECONDS (StartTest divides by 60 to
          // show minutes; SessionService seeds timeLeft from totalDuration). Use the
          // value directly — the old `* 60` misread seconds as minutes.
          const totalDuration = session.totalDuration || 1800; // seconds (30-min default)
          const remainingTime = session.timeRemaining || session.timeLeft || totalDuration;
          
          
          // Update time remaining state with the value from session
          setTimeRemaining(remainingTime);
          
          // Start at first question, let user navigate manually with prev/next/palette
          setCurrentQuestionIndex(0);
          
          // Track visited questions
          const newVisitedQuestions = new Set<number>();
          visitedIndices.forEach(index => {
            // Only add valid indices (within bounds of questions array)
            if (index >= 0 && index < processedQuestions.length) {
              newVisitedQuestions.add(index);
            }
          });
          // Visited questions tracking removed
          
          // Update questions state
          setQuestions(processedQuestions);
          
          // Save question count for use outside this block
          questionCount = processedQuestions.length;

          // Set snackbar message inside the try block where we have access to processedQuestions
          setSnackbarMessage(`Test resumed with ${processedQuestions.length} questions`);
          setShowSnackbar(true);
          
        } catch (formattingError) {
          setError('Error preparing test questions. Please refresh the page.');
          setIsLoading(false);
          return;
        }
        
        // Set loading state to false at the end using questionCount from outside try block
        setIsLoading(false);
        
      } catch (error) {
        setError('Failed to load test session. Please try again later.');
        setIsLoading(false);
      } finally {
        isFetchingQuestionsRef.current = false;
      }
    };
    
    loadSessionAndQuestions();
  }, [sessionId, dispatch, navigate]);

  // Function to track visited questions
  const trackCurrentQuestionAsVisited = useCallback(() => {
    
    // Visited questions tracking removed
    
    // Also mark the question object as visited for rendering
    setQuestions(prevQuestions => {
      if (prevQuestions && prevQuestions[currentQuestionIndex]) {
        const updatedQuestions = [...prevQuestions];
        updatedQuestions[currentQuestionIndex].isVisited = true;
        return updatedQuestions;
      }
      return prevQuestions;
    });
  }, [currentQuestionIndex]);

  const handleSubmitTest = useCallback(async () => {
    try {
      if (!sessionId) {
        return;
      }

      // Add confirmation for unanswered questions
      const answeredQuestions = questions.filter(q => q.selectedOption !== undefined);
      const unansweredCount = questions.length - answeredQuestions.length;
      
      if (unansweredCount > 0) {
        const confirmSubmit = window.confirm(
          `You have ${unansweredCount} unanswered questions. Are you sure you want to submit?`
        );
        if (!confirmSubmit) return;
      }

      setIsLoading(true);

      // Submit the test, capturing the time remaining so a later restart can
      // resume with exactly this much time left.
      const result = await endTestSession(sessionId, timeRemainingRef.current);
      
      if (result?.status === 'success') {
        // For mobile classroom, navigate back to dashboard; otherwise to results
        navigate(isMobileClassroom ? '/dashboard' : '/test-results');
      } else {
        setError('Failed to submit test. Please try again.');
      }
    } catch (error) {
      setError('Failed to submit test. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, navigate, questions]);



  // Effect to mark the current question as visited
  useEffect(() => {
    if (!sessionId || questions.length === 0) return;
    
    trackCurrentQuestionAsVisited();
  }, [currentQuestionIndex, sessionId, trackCurrentQuestionAsVisited]);

  
  // Separate effect to track question views in the backend
  useEffect(() => {
    if (!sessionId || questions.length === 0) return;
    
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;
    
    // Skip if this is the first render and previousQuestionId is not set
    // This prevents the initial API call when no question was actually navigated to
    if (previousQuestionId === '') {
      setPreviousQuestionId(currentQuestion.id);
      return;
    }
    
    // Store the IDs in local variables to avoid closure issues
    const currentQuestionId = currentQuestion.id;
    const prevQuestionId = previousQuestionId;
    
    // Only track if we actually changed questions
    if (currentQuestionId === prevQuestionId) return;
    
    // Track this question view in the backend as non-blocking call
    
    // Fire and forget - no need to wait for response
    trackQuestionLoad(sessionId, {
      previousQuestionId: prevQuestionId,
      questionId: currentQuestionId
    });
    
    // Update previous question ID for next tracking
    setPreviousQuestionId(currentQuestionId);
    
  }, [currentQuestionIndex, questions.length, sessionId, previousQuestionId]);
  
  // Keep the ref in sync so submit can read the current remaining time.
  useEffect(() => {
    timeRemainingRef.current = timeRemaining;
  }, [timeRemaining]);

  // Timer countdown effect
  useEffect(() => {
    if (isLoading || questions.length === 0) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          // Auto-submit test when time runs out
          handleSubmitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isLoading, questions.length, handleSubmitTest]);

  // Cleanup audio cache when component unmounts or test ends
  useEffect(() => {
    return () => {
      audioPreloader.clearCache();
    };
  }, []);

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      // Visited questions tracking removed
      // Update the question object's isVisited property
      setQuestions(prevQuestions => {
        const updatedQuestions = [...prevQuestions];
        if (updatedQuestions[currentQuestionIndex]) {
          updatedQuestions[currentQuestionIndex].isVisited = true;
        }
        return updatedQuestions;
      });
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Visited questions tracking removed
      // Update the question object's isVisited property
      setQuestions(prevQuestions => {
        const updatedQuestions = [...prevQuestions];
        if (updatedQuestions[currentQuestionIndex]) {
          updatedQuestions[currentQuestionIndex].isVisited = true;
        }
        return updatedQuestions;
      });
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleQuestionSelect = (index: number) => {
    // Visited questions tracking removed
    // Update the question objects' isVisited property
    setQuestions(prevQuestions => {
      const updatedQuestions = [...prevQuestions];
      if (updatedQuestions[currentQuestionIndex]) {
        updatedQuestions[currentQuestionIndex].isVisited = true;
      }
      if (updatedQuestions[index]) {
        updatedQuestions[index].isVisited = true;
      }
      return updatedQuestions;
    });
    setCurrentQuestionIndex(index);
  };

  const handleMarkForReview = (marked: boolean) => {
    if (!sessionId) return;
    
    const questionToMark = questions[currentQuestionIndex];
    if (!questionToMark) return;
    
    // Update local state
    setQuestions(prev => prev.map((q, i) => 
      i === currentQuestionIndex ? { ...q, isMarkedForReview: marked } : q
    ));
    
    // Send to backend
    const updateReviewStatus = async () => {
      try {
        await markQuestionForReview(sessionId, {
          questionId: questionToMark.id,
          isMarked: marked
        });
      } catch (err) {
        setSnackbarMessage('Failed to mark question for review');
        setShowSnackbar(true);
      }
    };
    
    // Execute the async function
    updateReviewStatus();
  };

  // Handle answer selection and submit to backend
  const handleAnswerSelect = async (questionId: string, answerIndex: number) => {
    if (!sessionId) return;
    
    // Ensure the answer index is within valid range (0-3)
    const validAnswerIndex = Number.isInteger(answerIndex) && answerIndex >= 0 && answerIndex <= 3 
      ? answerIndex 
      : 0;
      
    
    // Update local state first for responsive UI
    setQuestions(prev => prev.map(q => 
      q.id === questionId ? { ...q, selectedOption: validAnswerIndex } : q
    ));
    
    // Optimistically update the session answers in Redux
    dispatch(updateSessionAnswer({
      questionId,
      answer: validAnswerIndex
    }));
    
    try {
      // Submit answer to backend with validated index
      await submitAnswer(sessionId, {
        questionId,
        answerIndex: validAnswerIndex
      });
    } catch (err) {
      setSnackbarMessage('Failed to save your answer');
      setShowSnackbar(true);
      
      // TODO: Consider reverting the optimistic update on error
      // This would require storing the previous state or refetching
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };
  
  // Show loading state
  if (isLoading && questions.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Show error state
  if (error && questions.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      </Box>
    );
  }

  // Fix the null check for selected student
  if (!selectedStudent) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="warning">Please select a student first</Alert>
      </Box>
    );
  }

  // Show loading message if no questions loaded yet
  if (questions.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  // Remove the unused questionsWithVisited variable
  // const questionsWithVisited = questions.map((question, index) => ({
  //   ...question,
  //   isVisited: visitedQuestions.has(index)
  // }));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Header selectedStudent={selectedStudent} />
  
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ flex: { xs: '1', md: '2.5' } }}>
            {currentQuestion && (
              <QuestionCard
                currentQuestion={{
                  id: parseInt(currentQuestion.id) || currentQuestionIndex + 1, // Convert string ID to number
                  text: currentQuestion.text,
                  options: currentQuestion.options,
                  correctAnswer: -1, // Placeholder as we don't have correct answers during test
                  userAnswer: currentQuestion.selectedOption, // Map to component expected property
                  isMarkedForReview: currentQuestion.isMarkedForReview,
                  mediaUrl: currentQuestion.mediaUrl // Pass mediaUrl to QuestionCard
                }}
                currentQuestionIndex={currentQuestionIndex}
                totalQuestions={questions.length}
                timeRemaining={timeRemaining}
                testColor={testColor}
                lightenColor={lightenColor}
                onAnswerSelect={(answerIndex) => handleAnswerSelect(currentQuestion.id, answerIndex)}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onMarkForReview={() => handleMarkForReview(!currentQuestion.isMarkedForReview)}
                seriesName={currentSession?.series || selectedTest?.title}
                grade={selectedStudent?.grade}
                subject={selectedTest?.subject}
              />
            )}
          </Box>

          <Box sx={{ flex: { xs: '1', md: '0.8' }, minWidth: { md: '200px' } }}>
            <TestSidebar
              questions={questions.map((q, index) => ({
                id: index,  // Use index as number instead of q.id as string
                text: q.text,
                options: q.options,
                correctAnswer: -1,
                isVisited: !!q.isVisited,
                userAnswer: q.selectedOption,
                isMarkedForReview: q.isMarkedForReview
              }))}
              currentQuestionIndex={currentQuestionIndex}
              onQuestionSelect={handleQuestionSelect}
              onSubmitTest={handleSubmitTest}
              sessionId={sessionId || ''}
              testTitle={testTitle}
              testSubject={selectedTest?.subject}
              timeRemaining={timeRemaining}
              totalTime={currentSession?.totalDuration ? currentSession.totalDuration * 60 : 30 * 60}  // Use session duration or 30 min default
            />
          </Box>
        </Box>
      </Container>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default TestTakingPage;